import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tag, Spin } from '@arco-design/web-react';
import { RootState } from '@/store';
import Loading from '@/components/Loading/HorizonLoading';
import { isMobile } from '@/utils/utils';
import { useScene } from '@/lib/useCommon';
import USER_AVATAR from '@/assets/img/userAvatar.png';
import WONIU_AVATAR from '@/assets/img/woniuAvatar.png';
import styles from './index.module.less';

function Conversation(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  const room = useSelector((state: RootState) => state.room);
  const { msgHistory = [], isFullScreen = false } = room;
  const { userId = '' } = useSelector((state: RootState) => state.room.localUser);
  // 移除未使用的isAIConnected变量
  const { isAITalking = false, isUserTalking = false } = useSelector((state: RootState) => state.room);
  
  // 新增状态追踪AI是否真正准备就绪
  const [aiActuallyReady, setAiActuallyReady] = useState(false);
  const { botName = '小蜗牛' } = useScene();
  const containerRef = useRef<HTMLDivElement>(null);

  // 优化AI就绪判断逻辑
  useEffect(() => {
    // 对于移动端，使用更宽松的判断条件
    if (isMobile()) {
      // 如果AI正在说话，说明已经准备就绪
      if (isAITalking) {
        setAiActuallyReady(true);
      } 
      // 或者消息历史有内容
      else if (msgHistory.length > 0) {
        setAiActuallyReady(true);
      }
    } else {
      // 桌面端保持原有判断
      setAiActuallyReady(msgHistory.length > 0);
    }
  }, [msgHistory, isAITalking, isMobile()]);

  const isUserTextLoading = (owner: string) => {
    return !!userId && owner === userId && isUserTalking;
  };

  const isAITextLoading = (owner: string) => {
    return !!botName && owner === botName && isAITalking;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const scrollToBottom = () => {
        container.scrollTop = container.scrollHeight - container.clientHeight;
      };
      requestAnimationFrame(scrollToBottom);
    }
  }, [msgHistory, isMobile()]);

  return (
    <div
      ref={containerRef}
      className={[
        styles.conversation,
        className || '',
        isFullScreen ? styles.fullScreen : '',
        isMobile() ? styles.mobileConversation : ''
      ].join(' ')}
      {...rest}
    >
      {/* 使用优化后的aiActuallyReady状态 */}
      {!aiActuallyReady ? (
        <div className={styles.aiReadying}>
          <Spin size={16} className={styles['aiReading-spin']} />
          AI 准备中, 请稍侯
        </div>
      ) : null}
      
      {Array.isArray(msgHistory) && msgHistory.map((msg, index) => {
        const { value = '', user = '', isInterrupted = false } = msg;
        const isUserMsg = !!userId && user === userId;
        const isRobotMsg = !!botName && user === botName;
        
        if (!isUserMsg && !isRobotMsg) {
          return <div key={`empty-msg-${index}`} style={{ display: 'none' }} />;
        }

        return (
          <div
            key={`msg-container-${index}`}
            className={styles.mobileLine}
            style={isMobile() && isUserMsg ? { justifyContent: 'flex-end' } : {}}
          >
            {!isMobile() && (
              <div className={styles.msgName}>
                <div className={styles.avatar}>
                  <img 
                    src={isUserMsg ? USER_AVATAR : WONIU_AVATAR} 
                    alt={isUserMsg ? '我的头像' : '小蜗牛头像'}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiByeD0iMTAiIGZpbGw9IiNmNWY1ZjUiLz4KPHRleHQgeD0iMTAiIHk9IjExIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXdlaWdodD0iQmFja2dyb3VuZCI+MjwvdGV4dD4KPC9zdmc+Cg==';
                    }}
                  />
                </div>
                {isUserMsg ? '我' : botName}
              </div>
            )}
            <div
              className={[
                styles.sentence,
                isUserMsg ? styles.user : styles.robot
              ].join(' ')}
              key={`msg-content-${index}`}
            >
              <div className={styles.content}>
                {value || <span className={styles.emptyContent}>暂无内容</span>}
                <div className={styles['loading-wrapper']}>
                  {aiActuallyReady && 
                   index === msgHistory.length - 1 && 
                   (isUserTextLoading(user) || isAITextLoading(user)) ? (
                    <Loading gap={3} className={styles.loading} dotClassName={styles.dot} />
                  ) : null}
                </div>
              </div>
              {!isUserMsg && isInterrupted ? (
                <Tag className={styles.interruptTag}>已打断</Tag>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Conversation;
